import dayjs from 'dayjs';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Virtuoso } from 'react-virtuoso';
import { BoardStage } from '../../../entities/board';
import { Lead } from '../../../entities/lead/model/types';
import { useLeads } from '../../../entities/lead';
import type { LeadsFilters } from '../../../entities/lead';
import { LeadCard } from '../../../entities/lead/ui/LeadCard';



interface KanbanColumnProps {
  stage: BoardStage;
  filters?: LeadsFilters;
  isFiltered: boolean;
  onCardClick: (id: string, title?: string) => void;
  draggingLeadId: string | null;
  isDropTarget: boolean;
  onDragStart: (leadId: string) => void;
  onDragEnd: () => void;
  onDragOverStage: (stageId: string) => void;
  onDragLeaveStage: (stageId: string) => void;
  onDropLead: (leadId: string, stageId: string) => Promise<void>;
  selectionMode: boolean;
  selectedIds: Set<string>;
  onToggleSelect: (leadId: string) => void;
}

const INITIAL_PAGE_SIZE = 10;
const LOAD_MORE_SIZE = 10;

const sortByUpdated = (leads: Lead[]) =>
  [...leads].sort((a, b) => dayjs(b.updated).valueOf() - dayjs(a.updated).valueOf());

const KanbanColumn: React.FC<KanbanColumnProps> = ({
  stage,
  filters,
  isFiltered,
  onCardClick,
  draggingLeadId,
  isDropTarget,
  onDragStart,
  onDragEnd,
  onDragOverStage,
  onDragLeaveStage,
  onDropLead,
  selectionMode,
  selectedIds,
  onToggleSelect,
}) => {
  const { fetchLeadsPage, subscribeColumnUpdates } = useLeads();
  const [columnLeads, setColumnLeads] = useState<Lead[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const isLoadingMoreRef = useRef(false);
  const hasMoreRef = useRef(false);
  const pageRef = useRef(1);
  const filterKey = JSON.stringify(filters ?? null);

  const { data: initialPageData, isLoading: isQueryLoading } = useQuery({
    queryKey: ['column', stage.id, filterKey],
    queryFn: () => fetchLeadsPage(stage.id, 1, INITIAL_PAGE_SIZE, filters),
    staleTime: 3_000,
  });

  const isLoading = isQueryLoading;

  useEffect(() => {
    if (!initialPageData) {
      pageRef.current = 1;
      hasMoreRef.current = false;
      setColumnLeads([]);
      setTotalCount(0);
      return;
    }
    setColumnLeads(initialPageData.items);
    setTotalCount(initialPageData.totalItems);
    hasMoreRef.current = initialPageData.items.length < initialPageData.totalItems;
    pageRef.current = 1;
  }, [initialPageData]);

  useEffect(() => {
    // при активном фильтре реалтайм отключаем — обновления могут не совпадать с фильтром
    if (isFiltered) return;

    return subscribeColumnUpdates(stage.id, (action, lead) => {
      if (action === 'delete') {
        setColumnLeads(prev => {
          if (!prev.some(l => l.id === lead.id)) return prev;
          setTotalCount(c => Math.max(0, c - 1));
          return prev.filter(l => l.id !== lead.id);
        });
        return;
      }

      if (lead.boardId === stage.id) {
        setColumnLeads(prev => {
          if (action === 'create') {
            if (prev.some(l => l.id === lead.id)) return prev;
            setTotalCount(c => c + 1);
            return sortByUpdated([lead, ...prev]);
          }
          // update
          if (prev.some(l => l.id === lead.id)) {
            return sortByUpdated(prev.map(l => l.id === lead.id ? lead : l));
          }
          // Карточка перемещена В эту колонку
          setTotalCount(c => c + 1);
          return sortByUpdated([lead, ...prev]);
        });
      } else if (action === 'update') {
        // Карточка перемещена ИЗ этой колонки
        setColumnLeads(prev => {
          if (!prev.some(l => l.id === lead.id)) return prev;
          setTotalCount(c => Math.max(0, c - 1));
          return prev.filter(l => l.id !== lead.id);
        });
      }
    });
  }, [stage.id, isFiltered, subscribeColumnUpdates]);

  const handleLoadMore = useCallback(async () => {
    if (isLoadingMoreRef.current || !hasMoreRef.current) return;
    isLoadingMoreRef.current = true;
    setIsLoadingMore(true);
    const nextPage = pageRef.current + 1;
    try {
      const { items, totalItems } = await fetchLeadsPage(stage.id, nextPage, LOAD_MORE_SIZE, filters);
      setColumnLeads(prev => {
        const existingIds = new Set(prev.map(l => l.id));
        const fresh = items.filter(l => !existingIds.has(l.id));
        const merged = sortByUpdated([...prev, ...fresh]);
        hasMoreRef.current = merged.length < totalItems;
        return merged;
      });
      setTotalCount(totalItems);
      pageRef.current = nextPage;
    } catch (err) {
      console.error('Failed to load more leads:', err);
    } finally {
      isLoadingMoreRef.current = false;
      setIsLoadingMore(false);
    }
  }, [stage.id, fetchLeadsPage, filters]);

  const displayLeads = columnLeads;
  const displayCount = totalCount;

  const c = stage.colors;
  const colClass = 'col';

  return (
    <div className={colClass}>
      <div className="col-header" style={c ? { background: c.headerBg } : undefined}>
        <div className="col-hi">
          <div className="col-dot" style={c ? { background: c.dotBg } : undefined} />
          <div className="col-name" style={c ? { color: c.nameColor } : undefined}>{stage.name}</div>
        </div>
        <div className="col-cnt" style={c ? { background: c.cntBg, color: c.cntColor } : undefined}>{displayCount}</div>
      </div>
      <div
        className={`col-cards${isDropTarget ? ' col-cards-drop' : ''}`}
        onDragOver={event => {
          if (!draggingLeadId) return;
          event.preventDefault();
          event.dataTransfer.dropEffect = 'move';
          onDragOverStage(stage.id);
        }}
        onDragEnter={() => {
          if (draggingLeadId) onDragOverStage(stage.id);
        }}
        onDragLeave={() => onDragLeaveStage(stage.id)}
        onDrop={async event => {
          if (!draggingLeadId) return;
          event.preventDefault();
          await onDropLead(draggingLeadId, stage.id);
        }}
      >
        {isLoading ? (
          <div className="col-spinner">Загрузка...</div>
        ) : (
          <Virtuoso
            style={{ flex: 1, minHeight: 0 }}
            data={displayLeads}
            endReached={handleLoadMore}
            overscan={300}
            itemContent={(_, lead) => (
              <div style={{ paddingBottom: 8 }}>
                <LeadCard
                  lead={lead}
                  onClick={onCardClick}
                  isDragging={draggingLeadId === lead.id}
                  onDragStart={onDragStart}
                  onDragEnd={onDragEnd}
                  selectable={selectionMode}
                  selected={selectedIds.has(lead.id)}
                  onToggleSelect={onToggleSelect}
                />
              </div>
            )}
            components={{
              Footer: () => isLoadingMore ? (
                <div className="col-spinner" style={{ paddingBottom: 8 }}>Загрузка...</div>
              ) : null,
            }}
          />
        )}
      </div>
    </div>
  );
};

export { KanbanColumn };
